import { Badge, Flex, Switch, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { useHasRole, useAppToast, usePendingRows } from '../../../helpers'
import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { CommercialConsultant } from '../../../services/CommercialConsultant/types'
import CommercialConsultantFormModal from './CommercialConsultantFormModal'

// A tela nasce só com os ativos: a maior parte da lista é de consultores que já saíram.
const INITIAL_STATUS = 'active'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' }
]

const getLabelsOfAttendanceType = (consultant: CommercialConsultant) => {
  const [onlineRecorded, onlineLive] = [consultant.is_online_recorded_course, consultant.is_online_live_course]

  if (!onlineRecorded && !onlineLive) {
    return 'Nenhuma categoria'
  }

  const labels = []
  if (onlineLive)
    labels.push(
      <Badge key="live" colorScheme="purple">
        Presencial/Ao Vivo
      </Badge>
    )
  if (onlineRecorded)
    labels.push(
      <Badge key="recorded" colorScheme="blue">
        Gravado
      </Badge>
    )

  return labels
}

export default function CommercialConsultantsPage() {
  const service = new CommercialConsultantService()
  const toast = useAppToast()
  const [consultants, setConsultants] = useState<CommercialConsultant[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(INITIAL_STATUS)
  const [filtered, setFiltered] = useState<CommercialConsultant[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<CommercialConsultant | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<CommercialConsultant | null>(null)
  const savingRow = usePendingRows()
  const [isDeleting, setIsDeleting] = useState(false)
  const hasRole = useHasRole()
  const permissions = {
    addConsultant: hasRole('add_consultant'),
    editConsultant: hasRole('edit_consultant'),
    toggleConsultant: hasRole('toggle_consultant'),
    deleteConsultant: hasRole('delete_consultant')
  }

  useEffect(() => {
    loadConsultants()
  }, [])

  useEffect(() => {
    setFiltered(
      consultants.filter(
        (c) =>
          (status === '' || c.enabled === (status === 'active')) &&
          (c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase()))
      )
    )
  }, [search, status, consultants])

  const handleDelete = async () => {
    if (!deleting) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(deleting.id)
      toast.success('Consultor excluído')
      setIsDeleteOpen(false)
      loadConsultants()
    } catch (error) {
      toast.error('Erro ao excluir consultor', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const loadConsultants = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      setConsultants(await service.list())
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os consultores', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateEnabled = async (enabled: boolean, id: number) => {
    try {
      await service.updateEnabled(id, enabled)

      toast.success(`Consultor ${enabled ? 'ativado' : 'desativado'} com sucesso`)

      setConsultants((prev) =>
        prev.map((consultant) => (consultant.id === id ? { ...consultant, enabled } : consultant))
      )
    } catch (error) {
      toast.error('Não foi possível atualizar o consultor', error)
    }
  }

  const handleUpdateShowOnForms = async (show_on_forms: boolean, id: number) => {
    try {
      await service.updateShowOnForms(id, show_on_forms)

      toast.success(`Consultor ${show_on_forms ? 'inserido nos' : 'removido dos'} formulários com sucesso`)

      setConsultants((prev) =>
        prev.map((consultant) => (consultant.id === id ? { ...consultant, show_on_forms } : consultant))
      )
    } catch (error) {
      toast.error('Não foi possível atualizar o consultor', error)
    }
  }

  return (
    <>
      <PageTitle title="Consultores Comerciais" description="Equipe comercial e a distribuição de atendimentos" />
      <FilterBar
        onClear={() => {
          setSearch('')
          setStatus(INITIAL_STATUS)
        }}
        isDirty={search !== '' || status !== INITIAL_STATUS}
      >
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nome ou e-mail"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Categorias</Th>
              <Th>Exibir em formulários de leads</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="consultores" onRetry={loadConsultants} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="consultor" />
            ) : (
              filtered.map((consultant) => (
                <Tr key={consultant.id}>
                  <Td>{consultant.id}</Td>
                  <Td>{consultant.name}</Td>
                  <Td>{consultant.email}</Td>
                  <Td>
                    <Flex gap={2}>{getLabelsOfAttendanceType(consultant)}</Flex>
                  </Td>
                  <Td>
                    <Switch
                      isChecked={consultant.show_on_forms}
                      onChange={(e) =>
                        savingRow.run(consultant.id, () => handleUpdateShowOnForms(e.target.checked, consultant.id))
                      }
                      isDisabled={savingRow.isPending(consultant.id) || !permissions.editConsultant}
                    />
                  </Td>
                  <Td>
                    <Switch
                      isChecked={consultant.enabled}
                      onChange={(e) =>
                        savingRow.run(consultant.id, () => handleUpdateEnabled(e.target.checked, consultant.id))
                      }
                      isDisabled={savingRow.isPending(consultant.id) || !permissions.toggleConsultant}
                    />
                  </Td>
                  <Td>
                    {permissions.editConsultant && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setEditing(consultant)
                          setIsFormOpen(true)
                        }}
                      />
                    )}
                    {permissions.deleteConsultant && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeleting(consultant)
                          setIsDeleteOpen(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <CommercialConsultantFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadConsultants()
        }}
        consultant={editing}
      />
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        action="delete"
        entity="consultor"
        name={deleting?.name}
        isLoading={isDeleting}
      />

      {permissions.addConsultant && (
        <ActionFab
          label="Adicionar consultor comercial"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        />
      )}
    </>
  )
}
